import React, { useState } from 'react';

const mono = { fontFamily: "'DM Mono', monospace" };
const sans = { fontFamily: "'DM Sans', sans-serif" };

const FIELDS = [
  { key: 'owner_role', label: 'Owner Role Description', type: 'textarea', placeholder: 'Describe your role in the business...' },
  { key: 'top_customers', label: 'Top Customers (Name + % of Revenue)', type: 'textarea', placeholder: 'Customer A - 25%\nCustomer B - 15%\n...' },
  { key: 'revenue_mix', label: 'Revenue Mix', type: 'textarea', placeholder: 'Products: 60%, Services: 40%...' },
  { key: 'add_backs', label: 'Add-Backs', type: 'textarea', placeholder: 'List any owner add-backs or discretionary expenses...' },
  { key: 'employees', label: 'Number of Employees', type: 'text', placeholder: 'e.g. 45 full-time, 10 part-time' },
  { key: 'geography', label: 'Geography / Markets Served', type: 'text', placeholder: 'e.g. Mid-Atlantic, Southeast US' },
  { key: 'facility', label: 'Facility Details', type: 'textarea', placeholder: 'Owned/leased, square footage, location...' },
  { key: 'timeline', label: 'Desired Timeline', type: 'text', placeholder: 'e.g. 6-12 months' },
  { key: 'ideal_outcome', label: 'Ideal Outcome', type: 'textarea', placeholder: 'What does the ideal outcome look like for you?' },
  { key: 'deal_structure_preference', label: 'Deal Structure Preference', type: 'textarea', placeholder: 'Full sale, majority recapitalization, management buyout...' },
];

export default function IntakeFormFields({
  initialValues,
  onSubmit,
  readOnly = false,
  onSaveProgress,
  onRequestChanges,
}) {
  const [values, setValues] = useState(() => {
    const init = {};
    FIELDS.forEach((f) => {
      init[f.key] = (initialValues && initialValues[f.key]) || '';
    });
    return init;
  });
  const [busy, setBusy] = useState(false);

  const handleChange = (key, val) => {
    setValues((prev) => ({ ...prev, [key]: val }));
  };

  const handleSaveClick = async () => {
    if (!onSaveProgress) return;
    setBusy(true);
    try { await onSaveProgress(values); } finally { setBusy(false); }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!onSubmit) return;
    setBusy(true);
    try { await onSubmit(values); } finally { setBusy(false); }
  };

  const handleRequestClick = async () => {
    if (!onRequestChanges) return;
    setBusy(true);
    try { await onRequestChanges(); } finally { setBusy(false); }
  };

  const editableInputStyle = {
    ...sans, fontSize: 14, padding: '12px 14px',
    border: '1px solid rgba(7,25,42,0.10)', width: '100%',
    outline: 'none', background: '#FFFFFF', color: '#0A1828', minHeight: 48,
    resize: 'vertical', boxSizing: 'border-box',
  };

  const readOnlyInputStyle = {
    ...sans, fontSize: 14, padding: '12px 14px',
    border: '1px solid rgba(10,24,40,0.08)', width: '100%',
    outline: 'none', background: '#F8F7F5', color: '#0A1828', minHeight: 48,
    resize: 'vertical', boxSizing: 'border-box',
  };

  const inputStyle = readOnly ? readOnlyInputStyle : editableInputStyle;

  return (
    <form onSubmit={handleSubmit}>
      {FIELDS.map((field) => (
        <div key={field.key} style={{ marginBottom: 20 }}>
          <div style={{
            ...mono, fontSize: 7.5, letterSpacing: '0.16em',
            textTransform: 'uppercase', color: '#7A94A8', marginBottom: 6,
          }}>
            {field.label}
          </div>
          {field.type === 'textarea' ? (
            <textarea
              value={values[field.key]}
              onChange={(e) => handleChange(field.key, e.target.value)}
              placeholder={field.placeholder}
              rows={3}
              readOnly={readOnly}
              disabled={readOnly}
              style={inputStyle}
            />
          ) : (
            <input
              type="text"
              value={values[field.key]}
              onChange={(e) => handleChange(field.key, e.target.value)}
              placeholder={field.placeholder}
              readOnly={readOnly}
              disabled={readOnly}
              style={inputStyle}
            />
          )}
        </div>
      ))}

      {readOnly ? (
        <button
          type="button"
          onClick={handleRequestClick}
          disabled={busy}
          style={{
            ...mono, fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase',
            background: '#FFFFFF',
            color: 'rgba(10,24,40,0.60)',
            border: '1px solid rgba(10,24,40,0.15)',
            borderRadius: 0,
            padding: '0 24px',
            height: 44,
            width: '100%',
            cursor: busy ? 'not-allowed' : 'pointer',
            opacity: busy ? 0.6 : 1,
          }}
        >
          {busy ? 'Processing...' : 'REQUEST CHANGES'}
        </button>
      ) : (
        <div className="cp-intake-actions" style={{ display: 'flex', gap: 12 }}>
          <button
            type="button"
            onClick={handleSaveClick}
            disabled={busy}
            style={{
              ...mono, fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase',
              background: '#FFFFFF',
              color: '#0A1828',
              border: '1px solid rgba(10,24,40,0.20)',
              borderRadius: 0,
              padding: '0 24px',
              height: 44,
              flex: 1,
              minWidth: 0,
              cursor: busy ? 'not-allowed' : 'pointer',
              opacity: busy ? 0.6 : 1,
            }}
          >
            {busy ? 'Saving...' : 'SAVE PROGRESS'}
          </button>
          <button
            type="submit"
            disabled={busy}
            style={{
              ...mono, fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase',
              background: '#7A5230',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: 0,
              padding: '0 24px',
              height: 44,
              flex: 1,
              minWidth: 0,
              cursor: busy ? 'not-allowed' : 'pointer',
              opacity: busy ? 0.6 : 1,
            }}
          >
            {busy ? 'Submitting...' : 'SUBMIT ANSWERS'}
          </button>
        </div>
      )}
    </form>
  );
}
